import React, { createContext, useContext, useReducer } from "react";
import { APP_CONFIG } from "../constants";

// Initial state
const initialState = {
  userData: null,
  vitalsData: null,
  consultationData: null,
  podData: null,
  isLoading: false,
  error: null,
  currentCategory: null,
  completedCategories: [],
  categories: [],
  savedAnswers: {}, // Store answers for each assessment: { assessmentId: { questionId: answer } }
  measures: null, // Store vitals measures
};

// Action types
export const ACTION_TYPES = {
  SET_USER_DATA: "SET_USER_DATA",
  SET_VITALS_DATA: "SET_VITALS_DATA",
  SET_CONSULTATION_DATA: "SET_CONSULTATION_DATA",
  SET_POD_DATA: "SET_POD_DATA",
  SET_LOADING: "SET_LOADING",
  SET_ERROR: "SET_ERROR",
  RESET_STATE: "RESET_STATE",
  SET_CURRENT_CATEGORY: "SET_CURRENT_CATEGORY",
  SET_COMPLETED_CATEGORIES: "SET_COMPLETED_CATEGORIES",
  SET_CATEGORIES: "SET_CATEGORIES",
  SAVE_ANSWER: "SAVE_ANSWER",
  CLEAR_ANSWERS: "CLEAR_ANSWERS",
  SET_MEASURES: "SET_MEASURES",
};

// Reducer
const appReducer = (state, action) => {
  switch (action.type) {
    case ACTION_TYPES.SET_USER_DATA:
      return {
        ...state,
        userData: action.payload,
      };

    case ACTION_TYPES.SET_VITALS_DATA:
      return {
        ...state,
        vitalsData: action.payload,
      };

    case ACTION_TYPES.SET_CONSULTATION_DATA:
      return {
        ...state,
        consultationData: action.payload,
      };

    case ACTION_TYPES.SET_POD_DATA:
      return {
        ...state,
        podData: action.payload,
      };

    case ACTION_TYPES.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload,
      };

    case ACTION_TYPES.SET_ERROR:
      return {
        ...state,
        error: action.payload,
      };

    case ACTION_TYPES.RESET_STATE:
      return initialState;

    case ACTION_TYPES.SET_CURRENT_CATEGORY:
      return {
        ...state,
        currentCategory: action.payload,
      };

    case ACTION_TYPES.SET_COMPLETED_CATEGORIES:
      return {
        ...state,
        completedCategories: action.payload,
      };
    case ACTION_TYPES.SET_CATEGORIES:
      return {
        ...state,
        categories: action.payload,
      };

    case ACTION_TYPES.SAVE_ANSWER:
      const { assessmentId, questionId, answer } = action.payload;
      return {
        ...state,
        savedAnswers: {
          ...state.savedAnswers,
          [assessmentId]: {
            ...state.savedAnswers[assessmentId],
            [questionId]: answer,
          },
        },
      };

    case ACTION_TYPES.CLEAR_ANSWERS:
      const { assessmentId: clearAssessmentId } = action.payload;
      const newSavedAnswers = { ...state.savedAnswers };
      delete newSavedAnswers[clearAssessmentId];
      return {
        ...state,
        savedAnswers: newSavedAnswers,
      };

    case ACTION_TYPES.SET_MEASURES:
      return {
        ...state,
        measures: action.payload,
      };

    default:
      return state;
  }
};

// Create context
const AppContext = createContext();

// Provider component
export const AppProvider = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState);

  const setUserData = (userData) => {
    dispatch({ type: ACTION_TYPES.SET_USER_DATA, payload: userData });
  };

  const setVitalsData = (vitalsData) => {
    dispatch({ type: ACTION_TYPES.SET_VITALS_DATA, payload: vitalsData });
  };

  const setConsultationData = (consultationData) => {
    dispatch({
      type: ACTION_TYPES.SET_CONSULTATION_DATA,
      payload: consultationData,
    });
  };

  const setPodData = (podData) => {
    dispatch({
      type: ACTION_TYPES.SET_POD_DATA,
      payload: podData,
    });
  };

  const setLoading = (isLoading) => {
    dispatch({ type: ACTION_TYPES.SET_LOADING, payload: isLoading });
  };

  const setError = (error) => {
    dispatch({ type: ACTION_TYPES.SET_ERROR, payload: error });
  };

  const resetState = () => {
    dispatch({ type: ACTION_TYPES.RESET_STATE });
  };

  const setCurrentCategory = (currentCategory) => {
    dispatch({
      type: ACTION_TYPES.SET_CURRENT_CATEGORY,
      payload: currentCategory,
    });
  };

  const setCompletedCategories = (completedCategories) => {
    dispatch({
      type: ACTION_TYPES.SET_COMPLETED_CATEGORIES,
      payload: completedCategories,
    });
  };

  const setCategories = (categories) => {
    dispatch({ type: ACTION_TYPES.SET_CATEGORIES, payload: categories });
  };

  const saveAnswer = (assessmentId, questionId, answer) => {
    dispatch({
      type: ACTION_TYPES.SAVE_ANSWER,
      payload: { assessmentId, questionId, answer },
    });
  };

  const clearAnswers = (assessmentId) => {
    dispatch({
      type: ACTION_TYPES.CLEAR_ANSWERS,
      payload: { assessmentId },
    });
  };

  const setMeasures = (measures) => {
    dispatch({
      type: ACTION_TYPES.SET_MEASURES,
      payload: measures,
    });
  };

  const value = {
    ...state,
    setUserData,
    setVitalsData,
    setConsultationData,
    setPodData,
    setLoading,
    setError,
    resetState,
    setCurrentCategory,
    setCompletedCategories,
    setCategories,
    saveAnswer,
    clearAnswers,
    setMeasures,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

// Custom hook to use context
export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useAppContext must be used within an AppProvider");
  }
  return context;
};
